import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OutsideClickDirective } from '../../../directives/outside-click.directive';
import { CommonService } from '../../../services/common.service';
import { NotificationService } from '../../../services/notification.service';
import * as Types from '../../../shared/types';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'saved-concepts',
  standalone: true,
  imports: [CommonModule, FormsModule, OutsideClickDirective, MatTooltip],
  templateUrl: './saved-concepts.component.html',
  styleUrl: './saved-concepts.component.scss',
})
export class SavedConceptsComponent implements OnInit {
  @Output() conceptSelected = new EventEmitter<Types.SavedConceptFilter>();
  @Output() dropdownToggled = new EventEmitter<boolean>();

  savedConcepts: Types.SavedConceptFilter[] = [];
  selectedText = 'Saved concepts';
  isOptionSelected = false;
  dropdownOpened = false;
  searchQuery = '';
  loading = false;

  constructor(
    private commonService: CommonService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadConcepts();
  }

  loadConcepts(): void {
    this.loading = true;
    this.commonService.listConceptFilters({ includePrivate: true, globalOnly: false }).subscribe({
      next: (response) => {
        const data = response?.data ?? response;
        this.savedConcepts = data?.conceptFilters ?? data?.concept_filters ?? (Array.isArray(data) ? data : []);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.savedConcepts = [];
      },
    });
  }

  handleButtonClick(event: Event): void {
    event.stopPropagation();
    this.dropdownOpened = !this.dropdownOpened;
    this.dropdownToggled.emit(this.dropdownOpened);
    if (this.dropdownOpened) {
      this.loadConcepts();
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  clearSearch(): void {
    this.searchQuery = '';
  }

  onOutsideClick(): void {
    this.dropdownOpened = false;
    this.dropdownToggled.emit(false);
  }

  handleOptionSelect(event: Event, item: Types.SavedConceptFilter): void {
    event.stopPropagation();
    this.selectedText = item.name;
    this.isOptionSelected = true;
    this.dropdownOpened = false;
    this.dropdownToggled.emit(false);
    this.conceptSelected.emit(item);
  }

  handleDelete(event: Event, item: Types.SavedConceptFilter): void {
    event.stopPropagation();
    if (!item.conceptFilterId) {
      return;
    }

    this.commonService.deleteConceptFilter({ conceptFilterId: item.conceptFilterId }).subscribe({
      next: () => {
        this.notificationService.success('Concept filter', 'Deleted successfully.');
        this.savedConcepts = this.savedConcepts.filter((c) => c.conceptFilterId !== item.conceptFilterId);
        if (this.selectedText === item.name) {
          this.selectedText = 'Saved concepts';
          this.isOptionSelected = false;
        }
      },
      error: (error) => {
        const status = error?.status;
        const detail = error?.error?.detail;
        const message =
          status === 403
            ? 'Only the creator can delete this concept filter.'
            : (typeof detail === 'string' && detail) ||
              error?.error?.message ||
              error?.message ||
              'Failed to delete concept filter.';
        this.notificationService.error('Delete concept', message);
      },
    });
  }

  getFilteredConcepts(): Types.SavedConceptFilter[] {
    if (!this.searchQuery) {
      return this.savedConcepts;
    }
    const q = this.searchQuery.toLowerCase();
    return this.savedConcepts.filter(
      (item) =>
        item.name?.toLowerCase().includes(q) ||
        item.concept?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q)
    );
  }

  get truncatedText(): string {
    const maxLength = 18;
    return this.selectedText.length > maxLength
      ? this.selectedText.substring(0, maxLength) + '...'
      : this.selectedText;
  }

  visibilityLabel(item: Types.SavedConceptFilter): string {
    return item.isGlobal ? 'Global' : 'Private';
  }
}
